import log from 'loglevel'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  TextField,
  Typography
} from '@mui/material'
import {
  type Identifier,
  type RaRecord,
  type RowClickFunction,
  SimpleShowLayout,
  TextField as RaTextField,
  useTranslate,
  useUnselect,
  useUnselectAll,
  ShowBase,
  useDataProvider
} from 'react-admin'
import React from 'react'
import {
  compact,
  concat,
  difference,
  filter,
  find,
  head,
  includes,
  isNil,
  keys,
  map,
  size,
  split,
  template
} from 'lodash/fp'
import { SpotList } from 'components/SpotList'
import { isEmpty } from 'lodash'
import { type ConfigProps } from 'components/Dashboard'
import { getStep, useTourControl } from 'components/Step'
import { useTour } from '@reactour/tour'
import InfoIcon from '@mui/icons-material/Info'
import { ArrowRight } from '@mui/icons-material'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const SpotSelect: React.FC<
  {
    rowClick?: RowClickFunction
    multiple?: boolean
    opened?: boolean
    onSelect?: (selectedIds: Identifier[], data: RaRecord[]) => void
    onSelectAll?:
      | ((selectedIds: Identifier[], data: RaRecord[]) => void)
      | undefined
    onSelectAllBy?:
      | ((selectedIds: Identifier[], data: RaRecord[], allBy: string) => void)
      | undefined
    onSelectAllManagedBy?:
      | ((
          selectedIds: Identifier[],
          data: RaRecord[],
          allManagedBy: string
        ) => void)
      | undefined
    tourKey?: string
    allBy?: string | null | undefined
    allManagedBy?: string | null | undefined
    error?: string
    target?: string | null | undefined
    kind?: string
    withButton?: boolean | null | undefined
  } & ConfigProps
> = ({
  rowClick,
  multiple,
  opened,
  onSelect,
  onSelectAll,
  onSelectAllBy,
  onSelectAllManagedBy,
  config,
  allBy,
  allManagedBy,
  tourKey,
  error,
  target,
  kind,
  withButton
}) => {
  const { setIsOpen } = useTourControl(tourKey ?? 'SpotSelect')
  const { setSteps, setCurrentStep } = useTour()
  const steps = getStep(tourKey ?? 'SpotSelect')
  const { Spot: configSpot } = config
  const [records, setRecords] = React.useState<RaRecord[]>([])
  const translate = useTranslate()
  const [open, setOpen] = React.useState(!isNil(opened) && opened)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleClose = (): void => {
    setOpen(false)
  }
  const onClick = (): void => {
    setOpen(false)
  }
  const handleSelect = (selectedIds: Identifier[], data: RaRecord[]): void => {
    const currentIds = map((item: RaRecord) => item.id)(data)
    const others = filter((item: RaRecord) => !includes(item.id)(currentIds))(
      records
    )
    setRecords([
      ...others,
      ...filter((item: RaRecord) => includes(item.id)(selectedIds))(data)
    ])
    if (!isNil(onSelect)) onSelect(selectedIds, data)
  }
  const handleRowClick: RowClickFunction = async (
    id: Identifier,
    resource: string,
    record: RaRecord
  ) => {
    if (isNil(multiple) || !(multiple as boolean)) {
      setOpen(false)
    }
    setRecords([...records, record])
    // eslint-disable-next-line @typescript-eslint/return-await
    if (!isNil(rowClick)) return await rowClick(id, resource, record)
    return false
  }
  const onSelectClick = (): void => {
    setOpen(true)
  }
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const handleClosePopover = (): void => {
    setAnchorEl(null)
  }
  React.useEffect(() => {
    if (isNil(steps) || isNil(setSteps)) return
    setSteps(steps)
    setCurrentStep(0)
  }, [])
  const dataProvider = useDataProvider()
  React.useEffect(() => {
    if (isNil(target)) return
    dataProvider
      .getOne('mappings', { id: target })
      .then(({ data }) => {
        setRecords([data])
      })
      .catch(log.error)
  }, [target])
  const SpotInfo: React.FC<{ spin: string }> = ({ spin }) => {
    return (
      <SimpleShowLayout>
        {map((key: string) => {
          if (key === 'spin' && size(split(',')(spin)) > 1) {
            // @ts-expect-error ts(2339)
            return map.convert({ cap: false })(
              (eachSpin: string, index: number) => {
                return (
                  <RaTextField
                    key={`${key}-${index}`}
                    record={{ spin: eachSpin }}
                    source={key}
                    label={
                      index === 0
                        ? configSpot.fields[key].label ??
                          translate(`resources.mergedUsers.fields.${key}`)
                        : false
                    }
                  />
                )
              }
            )(split(',')(spin))
            // }
            // if (key === 'kind' && size(split('/')(kind)) > 1) {
            //   const kinds = join('/')(uniq(split('/')(kind)))
            //   // @ts-expect-error ts(2339)
            //   return map.convert({ cap: false })(
            //     (eachKind: string, index: number) => {
            //       return (
            //         <TextField
            //           key={`${key}-${index}`}
            //           record={{ kind: eachKind }}
            //           source={ke
            //           label={
            //             index === 0
            //               ? config.fields[key].label ??
            //                 translate(`resources.mergedUsers.fields.${key}`)
            //               : false
            //           }
            //         />
            //       )
            //     }
            //   )(split('/')(kinds))
          } else {
            return (
              <RaTextField
                key={key}
                source={key}
                label={
                  configSpot.fields[key].label ??
                  translate(`resources.mergedUsers.fields.${key}`)
                }
              />
            )
          }
        })(compact(keys(configSpot.fields)))}
      </SimpleShowLayout>
    )
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          flexDirection: 'row',
          gap: 1
        }}>
        {isNil(multiple) ||
        !(multiple as boolean) ||
        ((withButton ?? false) as boolean) ? (
          <Button variant="contained" onClick={onSelectClick} size="small">
            {translate('resources.spotSelect.buttons.trigger')}
          </Button>
        ) : (
          <IconButton onClick={onSelectClick}>
            <ArrowRight />
          </IconButton>
        )}
        <Box>
          <TextField
            variant="outlined"
            value={
              isEmpty(records)
                ? ''
                : size(records) === 1
                ? (head(records) as RaRecord)['name']
                : `${(head(records) as RaRecord)['name'] as string} ...`
            }
            inputProps={{
              readOnly: true
            }}
            sx={{ bgcolor: '#FEF7E3', flexGrow: 0 }}
          />
        </Box>
        {(isNil(multiple) || !(multiple as boolean)) && (
          <IconButton onClick={handleClick}>
            <InfoIcon
              fontSize="large"
              sx={{ boxShadow: 1, color: '#4f87f7' }}
            />
          </IconButton>
        )}
      </Box>
      {!isEmpty(records) && (
        <Popover
          open={!isNil(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}>
          <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper' }}>
            <ShowBase resource="mappings" id={(records[0] as any).id}>
              <SpotInfo spin={(records[0] as any).spin} />
            </ShowBase>
          </Box>
        </Popover>
      )}
      <Dialog onClose={handleClose} open={open} maxWidth="lg" fullWidth>
        <DialogTitle id="spotSelectDialog">
          {translate(`resources.spotSelect.dialog.title`)}
        </DialogTitle>
        <DialogContent>
          <SpotList
            resource="mappings"
            rowClick={handleRowClick}
            multiple={multiple}
            onSelect={handleSelect}
            onSelectAll={onSelectAll}
            onSelectAllBy={onSelectAllBy}
            onSelectAllManagedBy={onSelectAllManagedBy}
            config={config}
            setIsOpen={setIsOpen}
            allBy={allBy}
            allManagedBy={allManagedBy}
            kind={kind}
            saveFilter
          />
          {!isNil(error) && !isEmpty(error) && (
            <Typography color="red">{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClick}>
            {translate('resources.spotSelect.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
export const MultipleSpotSelect: React.FC<
  {
    resource: string
    selected: RaRecord[]
    setSelected: (selected: RaRecord[]) => void
    deleted?: boolean | undefined
    setDeleted?: ((deleted: boolean) => void) | undefined
    noAll?: boolean | undefined
    allBy?: string
    allManagedBy?: string | null | undefined
    setAllBy?: (value: { key: string; value: string }) => void
    limit?: number
    opened?: boolean
    withButton?: boolean
  } & ConfigProps
> = ({
  resource,
  selected,
  setSelected,
  deleted: deletedArg,
  setDeleted: setDeletedArg,
  noAll,
  allBy,
  allManagedBy,
  setAllBy,
  limit,
  config,
  opened,
  withButton
}) => {
  const [deletedLocal, setDeletedLocal] = React.useState(false)
  const [error, setError] = React.useState<string>('')
  let deleted = deletedArg
  let setDeleted = setDeletedArg
  if (isNil(deletedArg)) {
    deleted = deletedLocal
    setDeleted = setDeletedLocal
  }
  const unselectAll = useUnselectAll(resource)
  const unselect = useUnselect(resource)
  const translate = useTranslate()
  const handleRowClick: RowClickFunction = (
    id: Identifier,
    _resource: string,
    record: RaRecord
  ): any => {
    if (isNil(find((item: RaRecord) => item.id === id)(selected))) {
      setSelected([...selected, record])
    }
    return 'toggleSelection'
  }
  const handleDelete = (deleted: RaRecord): void => {
    setSelected(filter((item: RaRecord) => item.id !== deleted.id)(selected))
  }
  const handleSelect = (selectedIds: Identifier[], data: RaRecord[]): void => {
    if (!isNil(limit)) {
      if (size(selectedIds) > limit) {
        setError(
          template(translate('resources.spotSelect.errors.limit'))({ limit })
        )
        return
      } else {
        setError('')
      }
    }
    // log.debug({ selectedIds, data, selected })
    if (isNil(selected)) return
    if (isEmpty(selected) && isEmpty(selectedIds)) {
      unselectAll()
      return
    }
    const current = map((item: RaRecord) => item.id)(selected)
    // log.debug({
    //   selected,
    //   selectedIds,
    //   diff1: difference(current)(selectedIds),
    //   diff2: difference(selectedIds)(current)
    // })
    for (const id of difference(current)(selectedIds)) {
      const record = find((item: RaRecord) => item.id === id)(data)
      if (!isNil(record)) handleDelete(record)
    }
    for (const id of difference(selectedIds)(current)) {
      const record = find((item: RaRecord) => item.id === id)(data)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      if (!isNil(record)) handleRowClick(id, 'users', record)
    }
    if (!isNil(deleted) && (deleted as boolean)) {
      const diff = difference(selectedIds)(current)
      if (!isEmpty(diff)) unselect(diff)
      if (!isNil(setDeleted)) setDeleted(false)
    }
    const dataIds = map((item: RaRecord) => item.id)(data)
    if (isEmpty(selectedIds)) {
      setSelected([])
    } else {
      setSelected(
        concat(
          compact(
            map((item: RaRecord) =>
              includes(item.id)(dataIds) && !includes(item.id)(selectedIds)
                ? undefined
                : item
            )(selected)
          )
        )(
          compact(
            map((item: RaRecord) =>
              includes(item.id)(selectedIds) && !includes(item.id)(current)
                ? item
                : undefined
            )(data)
          )
        )
      )
    }
  }
  const handleSelectAll = (
    _selectedIds: Identifier[],
    data: RaRecord[]
  ): void => {
    if (!isEmpty(data) && !isNil(noAll) && (noAll as boolean)) return
    setSelected(data)
  }
  const handleSelectAllBy = (
    _selectedIds: Identifier[],
    data: RaRecord[],
    allByValue: string
  ): void => {
    if (!isNil(setAllBy) && !isNil(allBy)) {
      setAllBy({ key: allBy, value: allByValue })
    }
    setSelected(data)
  }
  const handleSelectAllManagedBy = (
    _selectedIds: Identifier[],
    data: RaRecord[],
    allManagedByValue: string
  ): void => {
    if (!isNil(setAllBy) && !isNil(allManagedBy)) {
      setAllBy({ key: allManagedBy, value: allManagedByValue })
    }
    setSelected(data)
  }
  React.useLayoutEffect(() => {
    unselectAll()
  }, [])
  return (
    <SpotSelect
      rowClick={handleRowClick}
      opened={opened ?? isEmpty(selected)}
      multiple
      onSelect={handleSelect}
      onSelectAll={
        !isNil(noAll) && (noAll as boolean) ? undefined : handleSelectAll
      }
      onSelectAllBy={isNil(allBy) ? undefined : handleSelectAllBy}
      onSelectAllManagedBy={isNil(allBy) ? undefined : handleSelectAllManagedBy}
      config={config}
      tourKey="MultipleSpotSelect"
      allBy={allBy}
      allManagedBy={allManagedBy}
      error={error}
      withButton={withButton}
    />
  )
}
